import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@src/environments/environment';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { MerlinMedia, MerlinMediaType, SearchEntityResults } from '@src/suite/common/store/merlin/models/ai.seach.models';
import { catchError, map, Observable, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PlaybookMerlinSearchService {
    public http = inject(HttpClient);
    public nomWebServicesJson = inject(NomWebServicesJson);

    searchEntity(queryText: string): Observable<SearchEntityResults> {
        const endpoint = `${environment.merlin_base_url}/APEntitySearch`;
        const data = {
            story: `${queryText}`,
        };

        return this.http
            .post<{ searchResult: SearchEntityResults }>(endpoint, data, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    accept: 'application/json',
                }),
                responseType: 'json',
                observe: 'response',
            })
            .pipe(
                map(res => res.body?.searchResult),
                catchError(() => {
                    return of({
                        hashtags: [],
                        people: [],
                        places: [],
                    });
                })
            );
    }

    generateMediaSearchTerm(story: string): Observable<string> {
        const endpoint = `${environment.merlin_base_url}/completion`;
        const data = {
            maxTokens: 450,
            prompt: 'Generate a search term that could be used to find images and video for the following story ',
            story: `${story}`,
        };

        return this.http
            .post<{ bot_answer: string }>(endpoint, data, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    accept: 'application/json',
                }),
                responseType: 'json',
                observe: 'response',
            })
            .pipe(
                // TODO: replacing noise words should be done on the service side of things not here but we are using a generic endpoint
                map(res => res.body?.bot_answer.replace(' images and videos', '').replace(' images videos', ''))
            );
    }

    searchPhotos(queryText: string): Observable<Array<MerlinMedia>> {
        return this.searchMedia(queryText, MerlinMediaType.PHOTO);
    }

    searchVideos(queryText: string): Observable<Array<MerlinMedia>> {
        return this.searchMedia(queryText, MerlinMediaType.PHOTO);
    }

    searchMedia(queryText: string, mediaType: MerlinMediaType): Observable<Array<MerlinMedia>> {
        const params = new URLSearchParams();
        params.append('text', queryText);
        params.append('type', mediaType);
        const endpoint = `${environment.merlin_base_url}/APAISearch`;

        const thumbnailSize = {
            width: 160,
            height: 160,
        };
        if (mediaType === MerlinMediaType.VIDEO) {
            thumbnailSize.width = 256;
            thumbnailSize.height = 89;
        }

        return this.http
            .get<{
                searchResult: {
                    hits: {
                        hits: Array<{
                            _source: MerlinMedia;
                        }>;
                    };
                };
            }>(endpoint, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                }),
                params: new HttpParams({ fromString: params.toString() }),
                responseType: 'json',
                observe: 'response',
            })
            .pipe(
                map(res => {
                    const data = res.body?.searchResult?.hits?.hits?.map(hint => ({
                        ...hint._source,
                        thumbnail: this.generateThumbnailUrl(hint._source, thumbnailSize.width, thumbnailSize.height),
                    }));
                    return data || [];
                })
            );
    }

    generateThumbnailUrl(media: MerlinMedia, width: number, height: number): string {
        return `${environment.merlin_media_base_url}/${media.itemid}.${media.recordsequencenumber}/thumbnail/${media.friendlykey}.jpg?s=${width}x${height}&tag=app_id=enps`;
    }
}
